// CB:BEGIN contractor-realtor-bid-form
/**
 * ContractorRealtorBidForm — full-page component matching ProjectDetails exactly.
 * Uses ContractorQuoteBidForm (the same form contractors use for regular projects).
 * Fetches GET /api/contractor/realtor-quotes/:id
 * Submits POST /api/contractor/realtor-quotes/:id/bid
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import SharedHeaderContractor from './SharedHeaderContractor';
import ContractorQuoteBidForm from './ContractorQuoteBidForm';
import type { QuoteBidPayload } from './ContractorQuoteBidForm';
import { useApi } from '../lib/useApi';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import { useAppUser } from '../context/AppUserContext';

interface QuoteDetail {
  id: string;
  propertyAddressLine1: string;
  propertyCity?: string | null;
  propertyState?: string | null;
  propertyZip?: string | null;
  status: string;
  maxBids: number;
  bidCount: number;
  expiresAt: string;
  distanceMiles?: number | null;
  contractorRadiusMiles?: number | null;
  roofReport?: {
    totalAreaSqFt?: number | null;
    predominantPitch?: string | null;
    stories?: number | null;
    suggestedWastePct?: number | null;
  } | null;
  myBid?: {
    id: string;
    amountCents: number;
    scopeNotes?: string | null;
    warrantyNotes?: string | null;
    timelineDays?: number | null;
    status: string;
    submittedAt?: string | null;
  } | null;
  roofReportId?: string | null;
}

interface Props {
  quoteRequestId: string;
  onNavigate: (screen: string) => void;
  onBidSubmitted?: () => void;
}

export function ContractorRealtorBidForm({ quoteRequestId, onNavigate, onBidSubmitted }: Props) {
  const { user } = useUser();
  const { get } = useApi();
  const authFetch = useAuthFetch();
  const { contractorsSelf } = useAppUser();

  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [contractorInfo, setContractorInfo] = useState<{ company: string | null; license: string | null; insurance: string | null; phone: string | null } | null>(null);

  // Mirror ProjectDetails pattern — reactive to async context load
  useEffect(() => {
    const c = contractorsSelf?.contractor;
    if (c) {
      setContractorInfo({
        company: c.company ?? null,
        license: c.licenseNumber ?? null,
        insurance: c.insurance ?? null,
        phone: c.phone ?? null,
      });
    }
  }, [contractorsSelf]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await get(`/api/contractor/realtor-quotes/${quoteRequestId}`);
      setQuote(data?.request as QuoteDetail);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load quote request');
    } finally {
      setLoading(false);
    }
  }, [quoteRequestId]);

  useEffect(() => { load(); }, [load]);

  // ── Submit handler — mirrors handleQuoteSubmitBid in ProjectDetails ──────────
  const handleSubmitBid = async (payload: QuoteBidPayload) => {
    if (submitting) return;
    const clerkId = user?.id;
    if (!clerkId) { alert('Please sign in to submit a bid.'); return; }

    // Canonical gate check — same source as POST /api/bids/submit
    if (!contractorsSelf?.canBid) {
      const br = contractorsSelf?.blockReason;
      if (br === 'verification_pending') {
        alert('Your verification is pending review. You can submit bids after approval.');
      } else if (br === 'agreement_required') {
        alert('You must accept the Contractor Platform Agreement before submitting bids.');
      } else {
        const go = window.confirm('You must complete account setup before submitting bids. Go to verification now?');
        if (go) onNavigate('contractor-verification');
      }
      return;
    }

    setSubmitting(true);
    try {
      // Derive timelineDays from start/end dates when available
      const timelineDays = (() => {
        try {
          const start = new Date(payload.earliestStartDate);
          const end = new Date(payload.estimatedCompletionDate);
          const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
          return days > 0 ? days : null;
        } catch { return null; }
      })();

      const resp = await authFetch(getApiUrl(`/api/contractor/realtor-quotes/${quoteRequestId}/bid`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amountCents: payload.amountCents,
          scopeNotes: payload.note || null,
          timelineDays,
          warrantyNotes: null,
        }),
      });

      if (!resp.ok) {
        let message = await resp.text();
        try { const j = JSON.parse(message); message = j.error || j.message || message; } catch {}
        throw new Error(message || `Bid submit failed (${resp.status})`);
      }

      setShowSubmittedModal(true);
      onBidSubmitted?.();
    } catch (e: any) {
      alert(e?.message ?? 'Bid submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  // No-op draft save (quote bids don't have a draft endpoint yet)
  const handleSaveDraft = async () => {};

  // ── Loading / error ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div className="content" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#6b7280' }}>Loading quote request...</div>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div className="content" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#dc2626', marginBottom: 16 }}>{error || 'Quote request not found'}</div>
          <button className="btn btn-primary" onClick={() => onNavigate('contractor-dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const slotsLeft = quote.maxBids - quote.bidCount;
  const isOpen = quote.status === 'COLLECTING_BIDS';
  const cityStateZip = [quote.propertyCity, quote.propertyState, quote.propertyZip].filter(Boolean).join(', ');

  // Gate banner — shown when contractor can't bid (unverified, no agreement, frozen)
  const canBid = contractorsSelf?.canBid !== false; // allow if still loading
  const blockReason = contractorsSelf?.blockReason ?? null;
  const gateBannerMessage = !canBid
    ? blockReason === 'verification_pending'
      ? 'Your verification is pending admin review. You can submit bids once approved.'
      : blockReason === 'agreement_required'
      ? 'You must accept the Contractor Platform Agreement before submitting bids.'
      : 'Complete your account setup to submit bids.'
    : null;

  // Shape the quote data as a project for ContractorQuoteBidForm
  const projectShape = {
    id: quote.id,
    address: quote.propertyAddressLine1,
    city: quote.propertyCity ?? null,
    state: quote.propertyState ?? null,
    zip: quote.propertyZip ?? null,
    status: quote.status,
    homeowner: { firstName: 'N/A', lastName: '' },
    roofReport: quote.roofReport ?? null,
    computedDistanceMiles: quote.distanceMiles ?? null,
  };

  return (
    <div className="container">
      <SharedHeaderContractor onNavigate={onNavigate} />

      {/* Submitted modal — identical to ProjectDetails */}
      {showSubmittedModal && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(420px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0 }}>Bid Submitted</h3>
            <p style={{ color: '#374151' }}>Your bid has been submitted successfully.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => { setShowSubmittedModal(false); onNavigate('contractor-dashboard'); }}>OK</button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>

          {/* ── Verification gate banner ── */}
          {gateBannerMessage && (
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '14px 20px', marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ color: '#92400e', fontWeight: 500 }}>⚠️ {gateBannerMessage}</div>
              {blockReason !== 'verification_pending' && (
                <button
                  className="btn btn-primary"
                  style={{ flexShrink: 0 }}
                  onClick={() => onNavigate('contractor-verification')}
                >
                  Complete Setup
                </button>
              )}
            </div>
          )}

          <div style={{ marginTop: 32, marginBottom: 32 }}>
            <div style={{ width: '100%' }}>
              <div>

                {/* ── Property info card — mirrors ProjectDetails exactly ────── */}
                <div className="dashboard-card" style={{ marginBottom: 24 }}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                      <h2 style={{ margin: 0 }}>{quote.propertyAddressLine1}</h2>
                    </div>
                    {cityStateZip && (
                      <div style={{ fontSize: 14, color: '#6b7280' }}>{cityStateZip}</div>
                    )}
                  </div>

                  {quote.distanceMiles != null && (
                    <p style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, margin: '0 0 12px 0' }}>
                      📍 {quote.distanceMiles.toFixed(1)} miles from you
                    </p>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                    <div>
                      <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> Realtor Quote Request</p>
                      <p style={{ margin: '0 0 6px 0' }}><strong>Bid slots remaining:</strong> {slotsLeft} of {quote.maxBids}</p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 6px 0' }}><strong>Status:</strong> {isOpen ? 'Accepting Bids' : quote.status}</p>
                      <p style={{ margin: '0 0 6px 0' }}><strong>Expires:</strong> {new Date(quote.expiresAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                  </div>

                  {/* Report link — same style as ProjectDetails */}
                  {quote.roofReportId && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await authFetch(getApiUrl(`/api/contractor/realtor-quotes/${quoteRequestId}/roof-report/download?format=json`));
                            if (!res.ok) { alert('Failed to load roof report'); return; }
                            const data = await res.json();
                            if (data.downloadUrl) window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
                            else alert('Report not available');
                          } catch { alert('Failed to load roof report'); }
                        }}
                        style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', color: '#374151', cursor: 'pointer', fontWeight: 500 }}
                      >
                        📄 View Roof Report (PDF)
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Bid form — same ContractorQuoteBidForm used for projects ── */}
                {isOpen ? (
                  <ContractorQuoteBidForm
                    project={projectShape}
                    projectId={quote.id}
                    contractorInfo={contractorInfo}
                    onSubmitBid={handleSubmitBid}
                    onSaveDraft={handleSaveDraft}
                    submitting={submitting}
                    templates={[]}
                  />
                ) : (
                  <div className="dashboard-card" style={{ textAlign: 'center', color: '#6b7280' }}>
                    <p style={{ margin: 0 }}>This quote request is no longer accepting bids.</p>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContractorRealtorBidForm;
// CB:END contractor-realtor-bid-form
